import { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Form, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowsRotate, faXmark } from '@fortawesome/free-solid-svg-icons';
import { observer } from 'mobx-react-lite';
import { useSearchParams } from 'react-router-dom';

import { getZonesLOV, type ZoneSummary } from 'api/zones/endpoints/getZonesLOV';
import FormatDateTime from 'components/common/FormatDateTime';
import { parseInt32 } from 'helpers/int32';
import { toError } from 'helpers/toError';
import { notify } from 'messages';

import { LiveLockCard } from './LiveLockCard';
import { LiveLocksModel } from './LiveLocksModel';
import './LiveLocks.scss';

/**
 * Live Locks: for each lock, the node instances holding it and the ones it is
 * blocking.
 *
 * Locks nothing holds and nothing waits on are omitted unless asked for, so an
 * empty page means nothing is contended. The page has no actions; every
 * instance links to the Node Instances list.
 *
 * `focus` is a URL parameter rather than another header filter, so a link to
 * one lock's card (e.g. from a node instance's locks) lands on it pinned to the
 * top, whatever the operator's own filter state and whether or not it is idle.
 */
/**
 * The URL owns focus, so a reload or a shared link lands on the same card.
 * Anything the backend would reject is treated as no focus at all: a rejected
 * lock_ids is a 400 for the whole page, not one missing card.
 */
const parseFocus = (focusParam: string | null): number | null => {
  const parsed = focusParam === null ? null : parseInt32(focusParam);
  return parsed !== null && parsed > 0 ? parsed : null;
};

const LiveLocks = observer(() => {
  const [searchParams, setSearchParams] = useSearchParams();
  const focusParam = searchParams.get('focus');
  // Created once, seeded with the focus the page opens on; the effect below
  // follows later URL changes.
  const [model] = useState(() => new LiveLocksModel(parseFocus(focusParam)));
  const [zones, setZones] = useState<readonly ZoneSummary[]>([]);

  useEffect(() => () => model.dispose(), [model]);

  useEffect(() => {
    model.setFocusLockId(parseFocus(focusParam));
  }, [focusParam, model]);

  useEffect(() => {
    getZonesLOV()
      .then(setZones)
      .catch((err) => {
        // An empty dropdown and an unfilterable one look identical, so say which this is.
        setZones([]);
        notify.error(`Zone filter unavailable: ${toError(err).message}`, { key: 'live-locks-zones' });
      });
  }, []);

  const setFocus = useCallback(
    (lockId: number | null) => {
      const next = new URLSearchParams(searchParams);
      if (lockId === null) {
        next.delete('focus');
      } else {
        next.set('focus', String(lockId));
      }
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  // Stable across renders that leave the URL alone, so the memoised cards skip
  // them; it changes with a focus change, which repaints every pin anyway.
  const togglePin = useCallback((lockId: number) => setFocus(lockId === model.focusLockId ? null : lockId), [setFocus, model]);

  return (
    <div className="live-locks">
      <div className="live-locks__header">
        <h4 className="mb-0">Live Locks</h4>

        <Form.Select
          className="live-locks__zone"
          aria-label="Zone"
          value={model.zoneId ?? ''}
          onChange={(event) => model.setZoneId(event.target.value === '' ? null : Number(event.target.value))}
        >
          <option value="">All zones</option>
          {zones.map((zone) => (
            <option key={zone.zone_id} value={zone.zone_id}>
              {zone.name}
            </option>
          ))}
        </Form.Select>

        <Form.Control
          className="live-locks__search"
          type="search"
          placeholder="Lock, process or instance id"
          aria-label="Search"
          value={model.search}
          onChange={(event) => model.setSearch(event.target.value)}
        />

        <Form.Check
          type="switch"
          id="live-locks-include-idle"
          className="live-locks__toggle"
          label="Show idle locks"
          checked={model.includeIdle}
          onChange={(event) => model.setIncludeIdle(event.target.checked)}
        />

        <div className="live-locks__header-actions">
          {model.asOf !== null && (
            <span className="text-muted small">
              As of <FormatDateTime value={model.asOf} />
            </span>
          )}
          <Button variant="link" className="live-locks__link" onClick={model.expandAll}>
            Expand all
          </Button>
          <Button variant="link" className="live-locks__link" onClick={model.collapseAll}>
            Collapse all
          </Button>
          <Button variant="outline-secondary" size="sm" onClick={() => void model.refreshPage()} title="Reload the page">
            <FontAwesomeIcon icon={faArrowsRotate} />
          </Button>
        </div>
      </div>

      {model.focusLockId !== null && (
        <Badge bg="secondary" className="live-locks__focus-chip">
          Focused: {model.focusedLock?.name ?? model.focusLockId}
          <Button variant="link" size="sm" className="live-locks__focus-clear" aria-label="Clear focus" onClick={() => setFocus(null)}>
            <FontAwesomeIcon icon={faXmark} />
          </Button>
        </Badge>
      )}

      {model.pageError !== null && <Alert variant="danger">{model.pageError}</Alert>}

      {model.truncated && <Alert variant="warning">More locks match than this page can show. Narrow the zone or search to see the rest.</Alert>}

      {model.isLoading && <Spinner animation="border" size="sm" />}

      {!model.isLoading && model.locks.length === 0 && model.pageError === null && (
        <p className="text-muted mb-0">{model.includeIdle ? 'No locks match.' : 'No lock is held or blocking anything.'}</p>
      )}

      {/* Dimmed rather than blanked while a newer answer is on its way, so the
          page never flickers empty between filter changes. */}
      <div className={model.isStale ? 'live-locks__cards live-locks__cards--stale' : 'live-locks__cards'}>
        {model.locks.map((lock) => (
          <LiveLockCard key={lock.lock_id} lock={lock} model={model} onTogglePin={togglePin} />
        ))}
      </div>
    </div>
  );
});

export default LiveLocks;
